import React, { forwardRef } from 'react';
import { Box, Grid } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import LessonHeroSection from './LessonHeroSection';
import LessonNavigationSidebar from './LessonNavigationSidebar';
import LessonNotesWidget from './LessonNotesWidget';
import LessonForumSection from './LessonForumSection';
import LessonVideoSection from './LessonVideoSection';
import LessonActionsSection from './LessonActionsSection';
import LessonContentBlocks from './LessonContentBlocks';
import MaterialsModal from './MaterialsModal';

/**
 * LessonDetail - unified component for displaying lesson content
 * Data fetching should be done in the parent page component
 * This component only handles UI rendering with configurable sections
 *
 * @param {Object} props
 * @param {Object} props.lesson - Lesson data (required)
 * @param {Object} props.courseData - Course data (optional, for course context)
 * @param {Object} props.currentModule - Current module data (optional)
 * @param {number} props.moduleIndex - Module index (optional)
 * @param {number} props.lessonIndex - Lesson index (optional)
 * @param {boolean} props.isBookmarked - Is lesson bookmarked
 * @param {Function} props.onToggleBookmark - Toggle bookmark handler
 * @param {Function} props.onScrollToReviews - Scroll to reviews handler
 * @param {boolean} props.showHero - Show hero section (default: true)
 * @param {boolean} props.showVideo - Show video section (default: true)
 * @param {boolean} props.showContent - Show content blocks (default: true)
 * @param {boolean} props.showActions - Show actions section (default: true)
 * @param {boolean} props.showForum - Show forum section (default: true)
 * @param {boolean} props.showNavigation - Show navigation sidebar (default: true)
 * @param {boolean} props.showNotes - Show notes widget (default: true)
 * @param {boolean} props.showMaterials - Show materials modal (default: true)
 * @param {Object} props.actionsProps - Props for LessonActionsSection
 * @param {Object} props.navigationProps - Props for LessonNavigationSidebar
 * @param {boolean} props.materialsModalOpen - Materials modal open state
 * @param {Function} props.onCloseMaterials - Close materials handler
 * @param {Function} props.onOpenMaterials - Open materials handler
 */
const LessonDetail = forwardRef(
  (
    {
      lesson,
      courseData = null,
      currentModule = null,
      moduleIndex = null,
      lessonIndex = null,
      isBookmarked = false,
      onToggleBookmark,
      onScrollToReviews = null,
      showHero = true,
      showVideo = true,
      showContent = true,
      showActions = true,
      showForum = true,
      showNavigation = true,
      showNotes = true,
      showMaterials = true,
      actionsProps = {},
      navigationProps = {},
      materialsModalOpen = false,
      onCloseMaterials,
      onOpenMaterials,
    },
    ref
  ) => {
    const { user } = useAuth();

    if (!lesson) {
      return null;
    }

    return (
      <Box ref={ref}>
        {/* Lesson Hero Section */}
        {showHero && (
          <LessonHeroSection
            courseData={courseData}
            currentModule={currentModule}
            currentLesson={lesson}
            moduleIndex={moduleIndex}
            lessonIndex={lessonIndex}
            isBookmarked={isBookmarked}
            onToggleBookmark={onToggleBookmark}
            onScrollToReviews={onScrollToReviews}
            onOpenMaterials={onOpenMaterials}
          />
        )}

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Main Content Column */}
          <Grid item xs={12} lg={showNavigation ? 8 : 12}>
            {/* Video Section */}
            {showVideo && <LessonVideoSection lesson={lesson} />}

            {/* Content Blocks */}
            {showContent && (
              <LessonContentBlocks
                lesson={lesson}
                contentBlocks={lesson.contentBlocks || []}
              />
            )}

            {/* Lesson Actions Section */}
            {showActions && (
              <LessonActionsSection
                lessonId={lesson._id}
                {...actionsProps}
              />
            )}

            {/* Forum Section */}
            {showForum && (
              <LessonForumSection
                lessonId={lesson._id}
                lessonTitle={lesson.title}
              />
            )}

            {/* Notes Widget - When navigation is hidden, show notes in main content */}
            {!showNavigation && showNotes && user && lesson._id && (
              <Box sx={{ mt: 3 }}>
                <LessonNotesWidget
                  lessonId={lesson._id}
                  initialNotes={lesson.userNotes || ''}
                />
              </Box>
            )}
          </Grid>

          {/* Sidebar - Desktop */}
          {showNavigation && (
            <Grid item xs={12} lg={4} sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: (theme) => theme.spacing(2),
                  [theme => theme.breakpoints.down('lg')]: {
                    position: 'static',
                  },
                }}
              >
                {/* Notes Widget */}
                {showNotes && user && lesson._id && (
                  <LessonNotesWidget
                    lessonId={lesson._id}
                    initialNotes={lesson.userNotes || ''}
                  />
                )}

                {/* Navigation Sidebar */}
                <LessonNavigationSidebar
                  courseData={courseData}
                  currentModule={currentModule}
                  currentLesson={lesson}
                  moduleIndex={moduleIndex}
                  lessonIndex={lessonIndex}
                  {...navigationProps}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Sidebar - Mobile (at bottom) */}
        {showNavigation && (
          <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 3 }}>
            {/* Notes Widget */}
            {showNotes && user && lesson._id && (
              <Box sx={{ mb: 2 }}>
                <LessonNotesWidget
                  lessonId={lesson._id}
                  initialNotes={lesson.userNotes || ''}
                />
              </Box>
            )}

            {/* Navigation Sidebar */}
            <LessonNavigationSidebar
              courseData={courseData}
              currentModule={currentModule}
              currentLesson={lesson}
              moduleIndex={moduleIndex}
              lessonIndex={lessonIndex}
              {...navigationProps}
            />
          </Box>
        )}

        {/* Materials Modal */}
        {showMaterials && (
          <MaterialsModal
            open={materialsModalOpen}
            onClose={onCloseMaterials}
            resources={lesson.resources || []}
          />
        )}
      </Box>
    );
  }
);

LessonDetail.displayName = 'LessonDetail';

export default LessonDetail;
